/**
 * ================================================================
 * SESSION INITIALIZER COMPONENT
 * ================================================================
 *
 * @component        SessionInitializer
 * @description      Handles session hydration and persistence at app initialization
 * @category         System Component
 *
 * @purpose
 * - Hydrate Redux state from storage on app load
 * - Set up session persistence middleware
 * - Handle session synchronization between Redux and storage
 *
 * @stateManagement
 * - Reads from storage once on mount
 * - Dispatches hydration action to Redux
 * - Subscribes to Redux changes for persistence
 *
 * @performance
 * - Runs only once on app initialization
 * - Uses useEffect with empty dependency array
 * - Minimal re-renders with proper memoization
 *
 * ================================================================
 */

import { useEffect, useRef } from 'react';

import {
  hydrateSessionFromStorage,
  persistPreferencesToStorage,
  persistSessionToStorage,
} from '@/lib/session/sessionManager';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  hydrateSession,
  selectIsHydrated,
  selectPreferences,
  selectSession,
} from '@/store/slices/userSlice';
import type { SessionInitializerProps } from '@/types/session';

export function SessionInitializer({ children }: SessionInitializerProps) {
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectSession);
  const preferences = useAppSelector(selectPreferences);
  const isHydrated = useAppSelector(selectIsHydrated);

  // Track if hydration has been attempted
  const hydrationAttempted = useRef(false);
  const lastPersistedSession = useRef<string>('');
  const lastPersistedPreferences = useRef<string>('');

  // ================================================================
  // INITIAL HYDRATION
  // ================================================================
  //
  // Purpose: Load session data from storage on app initialization
  // Timing: Runs once on component mount
  // Implementation: Dispatch hydration action with storage data
  // ================================================================

  useEffect(() => {
    if (!hydrationAttempted.current) {
      console.log('🚀 Initializing session hydration...');

      hydrationAttempted.current = true;

      try {
        const hydrationData = hydrateSessionFromStorage();

        // Convert hydration data to match Redux state structure
        const hydratePayload: any = {};

        if (hydrationData.session) {
          hydratePayload.session = hydrationData.session;
        }

        if (hydrationData.preferences) {
          hydratePayload.preferences = hydrationData.preferences;
        }

        dispatch(hydrateSession(hydratePayload));

        console.log('✅ Session hydration dispatched to Redux');
      } catch (error) {
        console.error('❌ Session hydration failed:', error);

        // Dispatch empty hydration to mark as attempted
        dispatch(hydrateSession({}));
      }
    }
  }, []); // Empty dependency array - run once on mount

  // ================================================================
  // SESSION PERSISTENCE
  // ================================================================
  //
  // Purpose: Sync Redux session changes to storage
  // Timing: Runs when session state changes
  // Implementation: Compare state and persist if changed
  // ================================================================

  useEffect(() => {
    // Only persist after hydration is complete
    if (!isHydrated) return;

    const sessionString = JSON.stringify(session);

    // Only persist if session has actually changed
    if (sessionString !== lastPersistedSession.current) {
      lastPersistedSession.current = sessionString;

      if (session.isAuthenticated && session.authToken && session.user) {
        const success = persistSessionToStorage(session);

        if (success) {
          console.log('💾 Session persisted to storage');
        } else {
          console.warn('⚠️ Failed to persist session to storage');
        }
      }
    }
  }, [session, isHydrated]);

  // ================================================================
  // PREFERENCES PERSISTENCE
  // ================================================================
  //
  // Purpose: Sync Redux preferences changes to storage
  // Timing: Runs when preferences state changes
  // Implementation: Compare state and persist if changed
  // ================================================================

  useEffect(() => {
    // Only persist after hydration is complete
    if (!isHydrated) return;

    const preferencesString = JSON.stringify(preferences);

    // Only persist if preferences have actually changed
    if (preferencesString !== lastPersistedPreferences.current) {
      lastPersistedPreferences.current = preferencesString;

      const success = persistPreferencesToStorage(preferences);

      if (success) {
        console.log('💾 Preferences persisted to storage');
      } else {
        console.warn('⚠️ Failed to persist preferences to storage');
      }
    }
  }, [preferences, isHydrated]);

  // ================================================================
  // SESSION ACTIVITY TRACKING
  // ================================================================
  //
  // Purpose: Update last activity timestamp for session timeout
  // Implementation: Listen to user interactions and update Redux
  // Note: This could be moved to a separate hook if needed
  // ================================================================

  useEffect(() => {
    if (!isHydrated || !session.isAuthenticated) return;

    const updateActivity = () => {
      // Dispatch activity update to Redux (which will trigger persistence)
      // This is handled by the main session state updates above
    };

    // Throttle activity updates to avoid excessive Redux dispatches
    let activityTimeout: NodeJS.Timeout;

    const throttledUpdateActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(updateActivity, 5000); // Update every 5 seconds max
    };

    // Listen to user activity events
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    events.forEach((event) => {
      document.addEventListener(event, throttledUpdateActivity, { passive: true });
    });

    return () => {
      clearTimeout(activityTimeout);
      events.forEach((event) => {
        document.removeEventListener(event, throttledUpdateActivity);
      });
    };
  }, [isHydrated, session.isAuthenticated]);

  // ================================================================
  // RENDER
  // ================================================================
  //
  // Purpose: Render children after hydration attempt
  // Implementation: Always render - hydration status tracked in Redux
  // Note: Components can check isHydrated selector if needed
  // ================================================================

  return <>{children}</>;
}

/**
 * ================================================================
 * END OF COMPONENT: SessionInitializer
 * ================================================================
 *
 * @exports          SessionInitializer - Session management wrapper
 * @dependencies     Redux hooks, session manager utilities
 * @usedBy           App.tsx - wraps entire application
 *
 * @lifecycle
 * - Mount: Hydrate session from storage
 * - Updates: Persist session and preferences changes
 * - Activity: Track user activity for session timeout
 *
 * @performance
 * - One-time hydration on app load
 * - Throttled activity tracking
 * - Change-based persistence (no unnecessary writes)
 *
 * ================================================================
 */
